import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { formatFixed } from '@/logic/statistics';
import type { BlunderVerdict } from '@/logic/adjustments/blunderVerdict';
import { useAppStore, selectActivePointCount } from '@/store/appStore';
import { useBlunderVerdict } from '@/store/selectors';
import type { ActiveTab, TransformationSolution } from '@/types';

export type StepStatus = 'todo' | 'done' | 'attention';

/** The three workflow screens. `pairs`/`transform` keep saved-project ids. */
export type WorkflowStepId = Extract<
  ActiveTab,
  'import' | 'pairs' | 'transform'
>;

export interface WorkflowStep {
  id: WorkflowStepId;
  label: string;
  status: StepStatus;
  /** Short live summary under the label, e.g. "RMS 0.034 · clean". */
  detail?: string;
}

export interface WorkflowInput {
  recordCount: number;
  fieldCount: number;
  pairCount: number;
  activePairCount: number;
  solution: TransformationSolution | null;
  solutionStale: boolean;
  verdict: BlunderVerdict;
  flaggedCount: number;
  detectionStale: boolean;
  transformedCount: number;
  transformedStale: boolean;
}

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Detection summary for the Adjust step, or null when it needs no attention. */
const detectionSummary = ({
  verdict,
  flaggedCount,
  detectionStale,
}: WorkflowInput): { status: StepStatus; text: string } => {
  if (detectionStale) return { status: 'attention', text: 'check outdated' };
  switch (verdict.state) {
    case 'clean':
      return { status: 'done', text: 'clean' };
    case 'agree':
    case 'differ':
      return { status: 'attention', text: `${flaggedCount} flagged` };
    case 'inconclusive':
      return { status: 'attention', text: 'inconclusive' };
    case 'single':
      return { status: 'attention', text: 'partial check' };
    default:
      return { status: 'todo', text: 'not checked' };
  }
};

/** Step status and detail, derived from session state (pure, for tests). */
export const deriveWorkflowSteps = (input: WorkflowInput): WorkflowStep[] => {
  const {
    recordCount,
    fieldCount,
    pairCount,
    activePairCount,
    solution,
    solutionStale,
    transformedCount,
    transformedStale,
  } = input;
  const dataLoaded = recordCount > 0 && fieldCount > 0;
  const solved = !!solution && !solution.error;

  let importDetail = 'Load coordinates';
  if (dataLoaded) importDetail = `${recordCount} Record · ${fieldCount} Field`;
  else if (recordCount > 0) importDetail = 'Field data needed';
  else if (fieldCount > 0) importDetail = 'Record data needed';

  let adjust: Pick<WorkflowStep, 'status' | 'detail'>;
  if (solved && solutionStale) {
    adjust = { status: 'attention', detail: 'Outdated · re-solve' };
  } else if (solved && solution.redundancy === 0) {
    adjust = { status: 'todo', detail: 'Exact fit · no redundancy' };
  } else if (solved) {
    const check = detectionSummary(input);
    adjust = {
      status: check.status,
      detail: `RMS ${formatFixed(solution.rmsTotal, 3)} · ${check.text}`,
    };
  } else if (pairCount > 0) {
    adjust = {
      status: 'todo',
      detail: `${plural(activePairCount, 'active pair')} · not solved`,
    };
  } else {
    adjust = {
      status: 'todo',
      detail: dataLoaded ? 'Review matches' : undefined,
    };
  }

  let exportStep: Pick<WorkflowStep, 'status' | 'detail'>;
  if (transformedCount > 0 && transformedStale) {
    exportStep = { status: 'attention', detail: 'Outdated · transform again' };
  } else if (transformedCount > 0) {
    exportStep = { status: 'done', detail: plural(transformedCount, 'point') };
  } else {
    exportStep = {
      status: 'todo',
      detail: solved && !solutionStale ? 'Ready to transform' : undefined,
    };
  }

  return [
    {
      id: 'import',
      label: 'Import',
      status: dataLoaded ? 'done' : 'todo',
      detail: importDetail,
    },
    { id: 'pairs', label: 'Adjust', ...adjust },
    { id: 'transform', label: 'Export', ...exportStep },
  ];
};

/** Live workflow steps for the stepper. */
export const useWorkflowSteps = (): WorkflowStep[] => {
  const state = useAppStore(
    useShallow((s) => ({
      recordCount: s.system1Points.length,
      fieldCount: s.system2Points.length,
      pairCount: s.pointPairs.length,
      activePairCount: selectActivePointCount(s),
      solution: s.solution,
      solutionStale: s.solutionStale,
      transformedCount: s.transformedPoints.length,
      transformedStale: s.transformedStale,
    }))
  );
  const { verdict, flaggedPointIds, detectionStale } = useBlunderVerdict();
  return useMemo(
    () =>
      deriveWorkflowSteps({
        ...state,
        verdict,
        flaggedCount: flaggedPointIds.length,
        detectionStale,
      }),
    [state, verdict, flaggedPointIds, detectionStale]
  );
};
